/**
 * InlineAudioPlayer Component
 *
 * Compact HTML5 audio player with custom styled controls (play/pause,
 * progress bar, duration label). Designed to fit inside a message bubble
 * in the operator console conversation view.
 *
 * Validates: Requirements 7.4
 * - WHEN an operator views a message with an audio attachment that has a
 *   playable URL, THE Operator_Console SHALL provide an inline audio player
 *   to listen to the original recording.
 */

import { useEffect, useRef, useState } from 'react';
import { tokens } from '../../design-system/tokens';

// ============================================
// TYPES
// ============================================

export interface InlineAudioPlayerProps {
  /** Audio URL (attachment.ref) */
  src: string;
  /** Estimated duration from metadata (attachment.durationSeconds) */
  durationSeconds?: number;
}

// ============================================
// HELPERS
// ============================================

/** Format seconds as M:SS */
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (typeof err === 'object' &&
      err != null &&
      'name' in err &&
      (err as { name: string }).name === 'AbortError')
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function InlineAudioPlayer({ src, durationSeconds }: InlineAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  /** Evita que el poll regenere `src` (token firmado) y corte la reproducción. */
  const [activeSrc, setActiveSrc] = useState(src);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [hasError, setHasError] = useState(false);
  const [isHoveringProgress, setIsHoveringProgress] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!src || src === activeSrc) return;
    // Solo refrescar URL si el clip no está en uso (poll con token nuevo).
    if (audio && (!audio.paused || audio.currentTime > 0)) return;
    setActiveSrc(src);
  }, [src, activeSrc]);

  // Sync duration from audio element when metadata loads
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      if (isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const onError = () => {
      setHasError(true);
      setIsPlaying(false);
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }
    void audio.play().catch((err: unknown) => {
      // pause()/cambio de src interrumpen play() con AbortError — no es fallo de carga
      if (isAbortError(err)) return;
      setHasError(true);
    });
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const displayTime = isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration);

  // Error state
  if (hasError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacing[2],
          padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
          borderRadius: tokens.borderRadius.md,
          backgroundColor: tokens.colors.neutral[100],
          width: '240px',
          maxWidth: '100%',
        }}
      >
        <span style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.neutral[500] }}>
          ⚠️ No se pudo cargar el audio
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing[2],
        padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
        borderRadius: tokens.borderRadius.md,
        backgroundColor: tokens.colors.neutral[100],
        width: '240px',
        maxWidth: '100%',
      }}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} src={activeSrc} preload="auto" />

      {/* Play/Pause button */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          borderRadius: tokens.borderRadius.full,
          border: 'none',
          backgroundColor: tokens.colors.primary[600],
          color: '#ffffff',
          cursor: 'pointer',
          flexShrink: 0,
          fontSize: '12px',
          lineHeight: 1,
          transition: tokens.transitions.fast,
        }}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>

      {/* Progress bar */}
      <div
        onClick={handleProgressClick}
        onMouseEnter={() => setIsHoveringProgress(true)}
        onMouseLeave={() => setIsHoveringProgress(false)}
        role="slider"
        aria-label="Progreso del audio"
        aria-valuenow={Math.round(currentTime)}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        tabIndex={0}
        onKeyDown={(e) => {
          const audio = audioRef.current;
          if (!audio || !duration) return;
          if (e.key === 'ArrowRight') {
            audio.currentTime = Math.min(duration, audio.currentTime + 5);
          } else if (e.key === 'ArrowLeft') {
            audio.currentTime = Math.max(0, audio.currentTime - 5);
          }
        }}
        style={{
          flex: 1,
          height: '6px',
          borderRadius: tokens.borderRadius.full,
          backgroundColor: tokens.colors.neutral[300],
          cursor: 'pointer',
          position: 'relative',
          transition: tokens.transitions.fast,
          opacity: isHoveringProgress ? 1 : 0.85,
        }}
      >
        {/* Filled portion */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${progress * 100}%`,
            borderRadius: tokens.borderRadius.full,
            backgroundColor: tokens.colors.primary[600],
            transition: 'width 100ms linear',
          }}
        />
      </div>

      {/* Duration label */}
      <span
        style={{
          fontSize: tokens.typography.fontSize.xs,
          color: tokens.colors.neutral[600],
          fontFamily: tokens.typography.fontFamily.mono,
          flexShrink: 0,
          minWidth: '32px',
          textAlign: 'right',
        }}
      >
        {displayTime}
      </span>
    </div>
  );
}

export default InlineAudioPlayer;
